import React, { useState, useEffect } from 'react';
import {
    FiChevronDown,
    FiChevronUp,
} from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import { getAuthContext } from '../../utils/jwt';
import { toast } from 'react-toastify';
import {
    setAppKeys,
    setAppKeysLoading,
    setAppKeysError,
    AppApiKey,
} from '../../features/apicredentials/apiKeysSlice';
import { BASE_URL } from '../../utils/constants';

const FiChevronDownComponent = FiChevronDown as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
}>;

const FiChevronUpComponent = FiChevronUp as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
}>;

const AppApiKeys: React.FC = () => {
    const dispatch = useDispatch();

    // Get app keys from Redux store
    const appKeys = useSelector((state: RootState) => state.apiKeys.appKeys);
    const loading = useSelector((state: RootState) => state.apiKeys.loading);

    const [sortConfig, setSortConfig] = useState<{
        key: keyof AppApiKey;
        direction: 'asc' | 'desc';
    } | null>(null);
    const [currentPage, setCurrentPage] = useState(1);

    const itemsPerPage = 6;

    // Fetch app keys from API
    const fetchAppKeys = async () => {
        try {
            dispatch(setAppKeysLoading(true));
            const { token, tenantId, userId } = getAuthContext();

            if (!token || !tenantId) {
                toast.error('Authentication required');
                dispatch(setAppKeysLoading(false));
                return;
            }


            const response = await fetch(
                `${BASE_URL}/api/settings/api-credentials/app-keys`,
                {
                    method: 'GET',
                    headers: {
                        'X-Tenant-Id': tenantId,
                        'X-User-Id': userId || '',
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to fetch app keys: ${response.status}`);
            }

            const data = await response.json();

            if (data.status === 'OK' && Array.isArray(data.data)) {
                dispatch(setAppKeys(data.data));
            } else {
                throw new Error('Invalid response format');
            }
        } catch (error: any) {
            toast.error(error.message || 'Failed to load app keys');
            // console.error('Fetch error:', error);
            dispatch(setAppKeysError(error.message || 'Failed to load app keys'));
        }
    };

    // Fetch on mount
    useEffect(() => {
        fetchAppKeys();
    }, []);

    // Format date for display
    const formatDate = (dateString: string | null) => {
        if (!dateString) return 'Never used';
        try {
            const date = new Date(dateString);
            return date.toLocaleString();
        } catch {
            return dateString;
        }
    };

    // Sorts API keys based on the specified key and direction
    const sortedKeys = React.useMemo(() => {
        if (!sortConfig) return appKeys;
        const sorted = [...appKeys].sort((a, b) => {
            let valA = a[sortConfig.key]?.toString().toLowerCase() ?? '';
            let valB = b[sortConfig.key]?.toString().toLowerCase() ?? '';

            if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
            if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [appKeys, sortConfig]);

    // Paginates the sorted API keys for display
    const totalPages = Math.ceil(sortedKeys.length / itemsPerPage);
    const paginatedKeys = sortedKeys.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Updates the sort configuration for a given key
    const requestSort = (key: keyof AppApiKey) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (
            sortConfig &&
            sortConfig.key === key &&
            sortConfig.direction === 'asc'
        ) {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Returns the appropriate sort icon based on the sort configuration
    const getSortIcon = (key: keyof AppApiKey) => {
        if (sortConfig?.key === key) {
            return sortConfig.direction === 'asc' ? (
                <FiChevronUpComponent size={14} />
            ) : (
                <FiChevronDownComponent size={14} />
            );
        }
        return <FiChevronDownComponent size={14} />;
    };

    // Status badge component
    const StatusBadge = ({ status }: { status: string }) => {
        const colors: Record<string, string> = {
            ACTIVE: 'bg-green-100 text-green-800',
            INACTIVE: 'bg-gray-100 text-gray-800',
            EXPIRED: 'bg-red-100 text-red-800',
            REVOKED: 'bg-red-100 text-red-800',
        };
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
                {status}
            </span>
        );
    };

    return (
        <div className='bg-white rounded-md mt-6 border border-gray-200 shadow-sm'>
            <div className='px-6 overflow-x-auto'>
                <table className='w-full text-left mt-4 border border-gray-200'>
                    <thead className='bg-gray-100'>
                        <tr
                            className='text-gray-600 text-sm border-b'
                            style={{ fontSize: '12px', color: '#657584' }}
                        >
                            <th
                                className='px-6 py-3 cursor-pointer font-normal'
                                onClick={() => requestSort('name')}
                            >
                                <div className='flex items-center gap-1'>
                                    NAME {getSortIcon('name')}
                                </div>
                            </th>
                            <th
                                className='px-6 py-3 cursor-pointer font-normal'
                                onClick={() => requestSort('workspaceId')}
                            >
                                <div className='flex items-center gap-1'>
                                    WORKSPACE {getSortIcon('workspaceId')}
                                </div>
                            </th>
                            <th className='px-6 py-3 font-normal'>SITE ID</th>
                            <th className='px-6 py-3 font-normal'>KEY PREFIX</th>
                            <th className='px-6 py-3 font-normal'>STATUS</th>
                            <th
                                className='px-6 py-3 cursor-pointer font-normal'
                                onClick={() => requestSort('createdAt')}
                            >
                                <div className='flex items-center gap-1'>
                                    CREATED {getSortIcon('createdAt')}
                                </div>
                            </th>
                            <th
                                className='px-6 py-3 cursor-pointer font-normal'
                                onClick={() => requestSort('lastUsedAt')}
                            >
                                <div className='flex items-center gap-1'>
                                    LAST USED {getSortIcon('lastUsedAt')}
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody style={{ color: '#3F5265', fontSize: '14px' }}>
                        {loading ? (
                            <tr>
                                <td colSpan={7} className='px-6 py-6 text-center'>
                                    <div className='flex justify-center items-center'>
                                        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-[#00899E]'></div>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            paginatedKeys.map((key) => (
                                <tr key={key.keyId} className='border-b hover:bg-gray-50'>
                                    <td className='px-6 py-4 font-medium'>{key.name}</td>
                                    <td className='px-6 py-4'>{key.workspaceId}</td>
                                    <td className='px-6 py-4'>{key.siteId}</td>
                                    <td className='px-6 py-4'>
                                        <code className='bg-gray-100 px-2 py-1 rounded text-sm font-mono'>
                                            {key.keyPrefix}
                                        </code>
                                    </td>
                                    <td className='px-6 py-4'>
                                        <StatusBadge status={key.status} />
                                    </td>
                                    <td className='px-6 py-4'>{formatDate(key.createdAt)}</td>
                                    <td className='px-6 py-4'>{formatDate(key.lastUsedAt)}</td>
                                </tr>
                            ))
                        )}
                        {!loading && paginatedKeys.length === 0 && (
                            <tr>
                                <td colSpan={7} className='px-6 py-6 text-center text-gray-500'>
                                    No App API Keys Found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Footer with Pagination */}
            <div className='flex items-center justify-between px-6 py-4 text-sm text-gray-500 border-t'>
                <span>
                    Showing {paginatedKeys.length} results out of {appKeys.length}
                </span>
                {totalPages > 1 && (
                    <div className='flex items-center gap-2'>
                        <button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage((prev) => prev - 1)}
                            className={`px-3 py-1 border rounded ${currentPage === 1
                                ? 'bg-gray-200 text-gray-300 cursor-not-allowed'
                                : 'bg-orange-500 text-white hover:bg-orange-600'
                                }`}
                        >
                            Prev
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => (
                            <button
                                key={i}
                                onClick={() => setCurrentPage(i + 1)}
                                className={`px-3 py-1 border rounded ${currentPage === i + 1
                                    ? 'bg-orange-500 text-white'
                                    : 'bg-white text-gray-600 hover:bg-gray-50'
                                    }`}
                            >
                                {i + 1}
                            </button>
                        ))}
                        <button
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage((prev) => prev + 1)}
                            className={`px-3 py-1 border rounded ${currentPage === totalPages
                                ? 'bg-gray-200 text-gray-300 cursor-not-allowed'
                                : 'bg-orange-500 text-white hover:bg-orange-600'
                                }`}
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AppApiKeys;